import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useGetIdentity } from "@refinedev/core";
import {
  PermissionGuard,
  NoPermissionFallback,
} from "../../components/PermissionGuard";
import { User } from "../../interfaces/user";

const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: currentUser } = useGetIdentity<User>();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 載入用戶詳情
  const loadUser = async () => {
    if (!id) {
      setError("無效的用戶 ID");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("未找到授權令牌");
      }

      const API_URL =
        import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com/";
      const response = await fetch(`${API_URL}/auth/users/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setUser(data.data.user);
      } else {
        throw new Error(data.message || "載入用戶詳情失敗");
      }
    } catch (err) {
      console.error("載入用戶詳情錯誤:", err);
      setError(err instanceof Error ? err.message : "載入用戶詳情失敗");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // 組件載入時載入用戶詳情
  useEffect(() => {
    loadUser();
  }, [id]);

  // 格式化角色顯示
  const formatRole = (role: string) => {
    const roleMap: Record<string, { label: string; color: string }> = {
      admin: { label: "管理員", color: "bg-purple-100 text-purple-800" },
      maintainer: { label: "維護人員", color: "bg-blue-100 text-blue-800" },
      user: { label: "一般用戶", color: "bg-gray-100 text-gray-800" },
    };
    return roleMap[role] || { label: role, color: "bg-gray-100 text-gray-800" };
  };

  // 格式化狀態顯示
  const formatStatus = (isActive: boolean) => (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
      }`}
    >
      {isActive ? "啟用" : "停用"}
    </span>
  );

  // 格式化日期顯示
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("zh-TW") +
      " " +
      date.toLocaleTimeString("zh-TW", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  return (
    <PermissionGuard
      requiredRoles={["admin", "maintainer"]}
      fallback={<NoPermissionFallback />}
    >
      <div className="px-4 sm:px-6 lg:px-8">
        {/* 面包屑導航 */}
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            <li>
              <div>
                <Link to="/users" className="text-gray-400 hover:text-gray-500">
                  <svg
                    className="flex-shrink-0 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="sr-only">返回</span>
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="flex-shrink-0 h-5 w-5 text-gray-300"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <Link
                  to="/users"
                  className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  用戶管理
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="flex-shrink-0 h-5 w-5 text-gray-300"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="ml-4 text-sm font-medium text-gray-500">
                  {user ? user.name : "用戶詳情"}
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* 內容區域 */}
        <div className="mt-6">
          {loading ? (
            <div className="bg-white shadow rounded-lg p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-500">載入中...</p>
            </div>
          ) : error ? (
            <div className="bg-white shadow rounded-lg p-8 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                載入失敗
              </h3>
              <p className="mt-1 text-sm text-gray-500">{error}</p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={loadUser}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  重新載入
                </button>
              </div>
            </div>
          ) : user ? (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              {/* 用戶基本資訊 */}
              <div className="px-4 py-5 sm:px-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-16 w-16">
                    <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-xl font-medium text-blue-600">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-6">
                    <h1 className="text-2xl font-bold text-gray-900">
                      {user.name}
                    </h1>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <div className="mt-2 flex items-center space-x-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          formatRole(user.role).color
                        }`}
                      >
                        {formatRole(user.role).label}
                      </span>
                      {formatStatus(user.isActive)}
                      {user.emailVerified && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          已驗證
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* 用戶詳細資訊 */}
              <div className="border-t border-gray-200">
                <dl>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      用戶 ID
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                        {user.id}
                      </code>
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      電子郵件
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {user.email}
                      {user.emailVerified ? (
                        <span className="ml-2 text-green-600">✓ 已驗證</span>
                      ) : (
                        <span className="ml-2 text-red-600">✗ 未驗證</span>
                      )}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      角色權限
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {formatRole(user.role).label}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      帳號狀態
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {formatStatus(user.isActive)}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      最後登入
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {user.lastLoginAt
                        ? formatDate(user.lastLoginAt)
                        : "從未登入"}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      API Key
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {user.apikey ? (
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                          {user.apikey}
                        </span>
                      ) : (
                        <span className="text-gray-500 italic">未設定</span>
                      )}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      建立時間
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {formatDate(user.createdAt)}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      最後更新
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {formatDate(user.updatedAt)}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* 操作按鈕 */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <Link
                  to="/users"
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  返回列表
                </Link>
                {/* Admin可以編輯任何用戶的完整資料 */}
                {currentUser?.role === 'admin' && user && (
                  // 不能編輯其他admin用戶
                  !(user.role === 'admin' && currentUser.id !== user.id) && (
                    <Link
                      to={`/users/${user.id}/admin-edit`}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm"
                    >
                      編輯用戶資料
                    </Link>
                  )
                )}
                {/* 一般用戶和維護人員角色時顯示編輯API Key按鈕 */}
                {user && (user.role === 'user' || user.role === 'maintainer') && (
                  <Link
                    to={`/users/${user.id}/edit`}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm"
                  >
                    編輯 API Key
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                找不到用戶
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                指定的用戶不存在或已被刪除。
              </p>
            </div>
          )}
        </div>
      </div>
    </PermissionGuard>
  );
};

export default UserDetailPage;
